import createWob from '../create-wob'
import i18n from 'scoreboard/components/i18n'
import option from 'scoreboard/components/bootslide/menu-icon-option'
import { sectionEndpoint } from 'scoreboard/components/bootslide/object-selector-menu'
import { AddExistingReportOption } from 'scoreboard/dashboards/widgets/report/report-widget-add-menu'
import _ from 'lodash'
import ChartData from 'scoreboard/charts/chart-data/chart-data'
import { filteredChartTypes } from 'scoreboard/charts/edit/charts-edit'
import ChartWidgetModel from 'scoreboard/dashboards/widgets/chart/chart-widget-model'
import ChartDialogEditor from 'scoreboard/charts/edit/chart-dialog-editor'
import ChartDesigner from 'scoreboard/charts/model/chart-designer'
import SCORECARD_CHART_LAYOUTS from 'scoreboard/charts/scorecard-chart-layouts'
import appDisplay from 'scoreboard/components/app-display'
import $ from 'jquery'
import globals from 'scoreboard/components/globals'
import { replaceChartIdsWithTempIds } from 'scoreboard/dashboards/edit/duplicate-wobs'
import ChartScorecardItemEndpoint from 'scoreboard/charts/edit/serie/chart-scorecard-item-endpoint'

const AddSavedChartOption = AddExistingReportOption.extend({
	selectorOptions() {
		let opt = AddExistingReportOption.prototype.selectorOptions.apply(this, arguments)

		opt.sectionSubstitution = i18n.get('dashboards.add.existingCharts')
		opt.section = 'charts'

		return opt
	},
	add(chart) {
		let periodId = this.bootslideMenu.options.getPeriodId()
		$.get(`api/charts/${chart.id}/data`, { calendarPeriodId: periodId })
			.then(({ chartDesigner }) => {
				let chartData = new ChartData({ dashboardData: this.model.data })

				const chart = new ChartDesigner(
					replaceChartIdsWithTempIds(chartDesigner),
					{ data: chartData }
				)

				chart.data.fetch({ chart, periodId }).then(() => {
					const widget = wrapChartDesignerInWidget(chart, this.bootslideMenu)

					createAndRenderWob({
						widget,
						bootslideMenu: this.bootslideMenu
					})
				})
			})
	}
})

function saved (bootslideMenu) {
	var label = 'dashboards.add.existingChart'
	return {
		label: i18n.get(label),
		content: option('add-widget-chart', label),
		contentEndpoint: sectionEndpoint(bootslideMenu, 'reports', AddSavedChartOption)
	}
}

function newChart (bootslideMenu) {
	// Iterate through CHART_TYPES so that we can build the menu for the new charts
	let chartCategories = []
	// #41880 use CHART_TYPES with items filtered out due to license
	for (let chartType of filteredChartTypes()) {
		let category = {
			label: i18n.get(chartType.optionType),
			target: []
		}
		for (let target of chartType.options) {
			let menuItem = {
				key: target.label,
				label: target.label,
				content: option(target.icon, i18n.get(target.label))
			}

			function add (extraSettings) {
				addChartWidget({
					bootslideMenu,
					chartLayoutSettings: _.extend({
						layout: target.type
					}, extraSettings)
				})
			}

			if (_.includes(SCORECARD_CHART_LAYOUTS, target.type)) {
				// #37217 if a scorecard chart is picked, give the user a scorecard
				// tree menu to select a scorecard item
				let ScorecardChartOption = ChartScorecardItemEndpoint.extend({
					add: function (node) {
						// Now that user has picked a scorecard item, we can
						// add the chart widget
						add({ scorecardItemId: node.id })
					}
				})

				_.extend(menuItem, {
					id: 'charts-pick-scorecard-item-' + target.type,
					label: i18n.get('dashboards.choose.chart.scorecardNode'),
					contentEndpoint: sectionEndpoint(bootslideMenu, 'scorecards', ScorecardChartOption)
				})
			} else {
				// Any other chart type can just be instantly added
				_.extend(menuItem, {
					target: () => add()
				})
			}

			category.target.push(menuItem)
		}

		chartCategories.push(category)
	}

	return {
		label: i18n.get('dashboards.add.newChart'),
		content: option('add-widget-new-chart', 'dashboards.add.newChart'),
		layout: 'tiles',
		categories: chartCategories
	}
}

/**
 * @param {String} chartLayout
 */
function addChartWidget({ chartLayoutSettings, bootslideMenu }) {
	const chartData = new ChartData({ dashboardData: bootslideMenu.model.data })
	const chart = new ChartDesigner({ chartLayoutSettings }, { data: chartData })
	const newChartEditor = new ChartDialogEditor({ model: chart })
	const widget = wrapChartDesignerInWidget(chart, bootslideMenu)

	// #40502 We prefetch the sample data so it show up in the editor
	let periodId = bootslideMenu.options.getPeriodId()
	let promise = widget.data.fetch({ periodId })
		.then(() => {
			// #40245 If the server adds some placeholder data, we need to grab that
			// We're doing it after the fetch promise resolves so that it's only
			// done on the initial render; it was previously being done after every
			// fetch, which caused some unnecessary chart double-renders on period
			// changes (killing the transitions, particularly for treemaps)
			let updatedChart = widget.data.chartDesignersByIds(chart.pick('id', 'tempId'))
			chart.set(updatedChart)

			// #59445 dashboard data doesn't include chart data anymore, so we
			// need to fetch the chart data
			return widget.chart.data.fetch({ periodId, chart })
		})
	appDisplay.dialog(promise, () => newChartEditor)

	newChartEditor.once('done', () => {
		createAndRenderWob({ widget, bootslideMenu })
	})
}

function createAndRenderWob ({ widget, bootslideMenu }) {
	// The chart has data already, either from the editor in new charts, or from
	// the chart data fetch for existing charts. So we can skip fetching again
	// and just render the chart content immediately
	let wobView = createWob(
		bootslideMenu.canvas, widget, { fetchData: false }, bootslideMenu)

	wobView.view.renderContent()
	wobView.view.applyToAccessoryViews(av => av.renderContent())
}

function wrapChartDesignerInWidget(chartDesigner, bootslideMenu) {
	let widget = new ChartWidgetModel({
		properties: {
			type: 'chart',
			chartTempId: chartDesigner.get('tempId')
		}
	}, { chart: chartDesigner })

	widget.maestro(bootslideMenu.canvas.dashboard.maestro)
	return widget
}

export { saved, newChart }